import { FirebaseError } from 'firebase/app'
import { type UserCredential } from 'firebase/auth'
import { useCallback } from 'react'

import { logError } from '~/src/utils/errors'
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
} from '~/src/utils/integrations/firebase'
import { unauthedApi } from '~/src/api'

type EmailAndPassword = { email: string; password: string }

const EmailAlreadyInUseErrorCode = 'auth/email-already-in-use'

/**
 * A wrapper around Firebase's `createUserWithEmailAndPassword` that handles errors
 * @example redirects to the sign in page if email is already in use
 */
export function useCreateFirebaseUser(): (params: EmailAndPassword) => Promise<UserCredential> {
  return useCallback(async ({ email, password }: EmailAndPassword) => {
    try {
      return await createUserWithEmailAndPassword(email, password)
    } catch (error) {
      if (!(error instanceof FirebaseError && error.code === EmailAlreadyInUseErrorCode)) {
        logError.veryHigh(error)
        throw error
      }
    }

    // If creating a user has failed, then try to sign in and continue.
    // If a user has tried to create a user multiple times in the same session, this can save us.
    try {
      return await signInWithEmailAndPassword(email, password)
    } catch {}

    // If that fails, then call backend endpoint FixFirebaseUser. The backend will attempt to safely remediate the
    // fix by clearing an orphaned Firebase user, if it applies. This might make the next
    // createUserWithEmailAndPassword succeed.
    await unauthedApi.post('users/fix-firebase-user', { json: { email } })

    try {
      return await createUserWithEmailAndPassword(email, password)
    } catch (error) {
      if (error instanceof FirebaseError && error.code === EmailAlreadyInUseErrorCode) {
        window.location.replace(`/sign-in?email=${encodeURIComponent(email)}`)
      }

      logError.veryHigh(error)
      throw error
    }
  }, [])
}
